import * as Telecom from '@servicetitan/telecom-rtcs-client';
import TasksModule from '@modules/TasksModule';
import { authState } from '@modules/AuthModule/state';
import getQueue from '../../lib/getQueue';
import log from '../../lib/log';
import requireUser from '../../lib/requireUser';
import { refreshQueueOnCacheMiss } from '../../lib/refreshQueue';
import isValidUser from '../../lib/isValidUser';
import ToastModule from '@modules/ToastModule';
import setQueueStatus from './setQueueStatus';
import { removeSessionFromQueue } from './queueSessionUtils';
import {
  discardStaleQueueStatus,
  enqueueQueueSessionDeltaEvent,
  hasPendingQueueDelta,
} from '../../lib/coalesceWorkspaceEvents';

async function handleQueueSessionDelivered(
  event: Telecom.QueueSessionDeliveredEvent,
) {
  if (!isValidUser(event.agentId)) {
    log('invalid userId:', event.agentId);
    return;
  }

  // Intentional before getQueue: a kicked snapshot must not apply even on cache miss.
  discardStaleQueueStatus(event.queueId);

  const queue = getQueue(event.queueId);

  log('rtcs:queue:delivered:' + queue?.name, event);

  /**
   * Handle simultaneous ring (aka Ring All)
   *
   * If the task is offered to the current user but accepted by another agent,
   * we need to delete the task so that the task won't get stuck in the UI and
   * keep track of call events for the task. One caveat is that we don't want to
   * do this for outbound calls - the user could have made an outbound call via
   * a queue and we would get this event as soon as a user in the queue answers the call.
   *
   * We don't need to check if the queue is set to simultaneous ring because
   * we currently don't have any other scenario where an offered task is accepted
   * by another agent.
   *
   */
  const task = TasksModule.getTask(event.conversationSid);
  const { user } = authState;
  const userId = user?.id ?? '';
  const deliveredUserId = (await requireUser(event.agentId)).id ?? '';

  // Skip deletion for transfer tasks — TransfersModule handles their lifecycle via polling
  const isTransferTask = !!task?.attributes?.transferStatus?.inProgress;

  if (
    task &&
    !task.isOutbound &&
    !isTransferTask &&
    userId !== deliveredUserId
  ) {
    if (task.attributes?.isAnswering === true) {
      // if the task was accepted by the current the current user but delivered to another agent, then we show a toast message.
      ToastModule.showToast({
        title: 'Call was answered by another agent',
        status: 'info',
      });
    }
    TasksModule.deleteTask(event.conversationSid);
  }

  const applyQueueMutation = (): void | Promise<void> => {
    const current = getQueue(event.queueId);
    if (!current) {
      return refreshQueueOnCacheMiss(event.queueId);
    }
    removeSessionFromQueue({
      queue: current,
      event,
      logPrefix: 'rtcs:queue:delivered',
      handlerName: 'handleQueueSessionDelivered',
    });
    setQueueStatus(current, event);
  };

  // Keep task-clear above immediate. If join/left is still buffered, FIFO the
  // waiting-list update behind it so a late join cannot re-add this session.
  if (hasPendingQueueDelta(event.queueId)) {
    return enqueueQueueSessionDeltaEvent(event.queueId, applyQueueMutation);
  }

  return applyQueueMutation();
}

export default handleQueueSessionDelivered;
